import { FormikHelpers, FormikValues } from "formik";
import { Answer } from "types/contentful";
import {
  OrderSummaryItem as OrderSummaryItemSnakeCase,
  SnakeToCamelCaseNested,
} from "@literati/kids-api";

import { ClubRecommendationResponse, Invoice } from "lib/api/checkout";
import { Subscription } from "lib/api/subscription";

import { StepLayoutProps } from "components/Step/StepLayout";

import { ShippingValues as TypeShippingValues } from "components/Shipping/shippingTypes";

export interface Step<State, Slug = string> {
  component: (state: State) => JSX.Element;
  id: string;
  slug: Slug;
}

export type State = Record<string, unknown>;

export type PrimitiveStepLayoutProps<Values extends FormikValues> = Omit<
  StepLayoutProps<Values>,
  "asForm" | "children" | "formId"
>;

export type FormikSubmitStep<StepValues> = (
  values: StepValues,
  formikHelpers?: FormikHelpers<StepValues>
) => void;

export type FormikSubmitFlow<FlowValues> = (
  values: FlowValues,
  formikHelpers?: FormikHelpers<any>
) => void;

export type ClubRecommendationResponseCamelCase =
  SnakeToCamelCaseNested<ClubRecommendationResponse>;

export enum ReaderProfileKey {
  FIXED_AGE = "reader-fixed-age",
  KIND = "kind",
  MONTH = "reader-birth-month",
  NAME = "reader-name",
  YEAR = "reader-birth-year",
  CALCULATED_AGE = "reader-calculated-age",
  SKIP_DUP_MODAL = "skipDupModal",
}
export type ReaderProfileKeyEnumValue = `${ReaderProfileKey}`;
export type ReaderProfileValues = {
  [ReaderProfileKey.KIND]: "reader-profile";
  [ReaderProfileKey.FIXED_AGE]: string;
  [ReaderProfileKey.MONTH]: string;
  [ReaderProfileKey.NAME]: string;
  [ReaderProfileKey.SKIP_DUP_MODAL]: boolean;
  [ReaderProfileKey.CALCULATED_AGE]?: number;
  [ReaderProfileKey.YEAR]: string;
};
export type ReaderProfileValuesPartial = Partial<ReaderProfileValues>;
export type ReaderProfileSubmit = FormikSubmitStep<ReaderProfileValues>;

export enum ShippingKey {
  KIND = "kind",
  CITY = "city",
  NAME = "name",
  POSTAL_CODE = "postalCode",
  PREVENT_ROUTING = "preventRouting",
  STATE = "state",
  STREET1 = "street1",
  STREET2 = "street2",
}
export type ShippingKeyEnumValue = `${ShippingKey}`;
export type ShippingValues = TypeShippingValues & {
  [ShippingKey.PREVENT_ROUTING]: boolean;
};
export type ShippingValuesPartial = Partial<ShippingValues>;
export type ShippingSubmit = FormikSubmitStep<ShippingValues>;

export enum RelationshipKey {
  KIND = "kind",
  RELATIONSHIP = "reader-relationship",
}
export type RelationshipKeyEnumValue = `${RelationshipKey}`;
export type RelationshipValues = {
  [RelationshipKey.KIND]: "relationship";
  [RelationshipKey.RELATIONSHIP]: string;
};
export type RelationshipValuesPartial = Partial<RelationshipValues>;
export type RelationshipSubmit = FormikSubmitStep<RelationshipValues>;

export enum ReadingLevelKey {
  KIND = "kind",
  GRADE_ADJUSTMENT = "reading-level-adjustment",
  GRADE_ADJUSTMENT_KEY = "reading-level-adjustment-grade",
}
export type ReadingLevelKeyEnumValue = `${ReadingLevelKey}`;
export type ReadingLevelValues = {
  [ReadingLevelKey.KIND]: "reading-level";
  [ReadingLevelKey.GRADE_ADJUSTMENT]: string;
  [ReadingLevelKey.GRADE_ADJUSTMENT_KEY]: string;
};
export type ReadingLevelValuesPartial = Partial<ReadingLevelValues>;
export type ReadingLevelSubmit = FormikSubmitStep<ReadingLevelValues>;

export enum GenderKey {
  KIND = "kind",
  GENDER = "reader-gender",
}
export type GenderKeyEnumValue = `${GenderKey}`;
export type GenderValues = {
  [GenderKey.KIND]: "gender";
  [GenderKey.GENDER]: string;
};
export type GenderValuesPartial = Partial<GenderValues>;
export type GenderSubmit = FormikSubmitStep<GenderValues>;

export enum GoalsKey {
  KIND = "kind",
  SELECTIONS = "reader-membership-goals",
  /** Free-text goal when "Other" is selected; merged into selections on submit. */
  OTHER_GOAL_TEXT = "reader-goals-other-text",
}
export type GoalsKeyEnumValue = `${GoalsKey}`;
export type GoalsValues = {
  [GoalsKey.KIND]: "goals";
  [GoalsKey.SELECTIONS]: string[];
  [GoalsKey.OTHER_GOAL_TEXT]: string;
};
export type GoalsValuesPartial = Partial<GoalsValues>;
export type GoalsSubmit = FormikSubmitStep<GoalsValues>;

export enum ThemePreferenceKey {
  KIND = "kind",
  THEME_PREFERENCES = "reader-theme-preference",
}
export type ThemePreferenceKeyEnumValue = `${ThemePreferenceKey}`;
export type ThemePreferenceValues = {
  [ThemePreferenceKey.KIND]: "reader-theme-preferences";
  [ThemePreferenceKey.THEME_PREFERENCES]: Answer[] | null;
};
export type ThemePreferenceValuesPartial = Partial<ThemePreferenceValues>;
export type ThemePreferenceSubmit = FormikSubmitStep<ThemePreferenceValues>;

export enum BudgetKey {
  KIND = "kind",
  BUDGET = "reader-budget",
}
export type BudgetKeyEnumValue = `${BudgetKey}`;
export type BudgetValues = {
  [BudgetKey.KIND]: "budget";
  [BudgetKey.BUDGET]: string;
};
export type BudgetValuesPartial = Partial<BudgetValues>;
export type BudgetSubmit = FormikSubmitStep<BudgetValues>;

export enum AdjustDeliveryScheduleKey {
  KIND = "kind",
  FREQUENCY = "frequency",
}
export type AdjustDeliveryScheduleEnumValue = `${AdjustDeliveryScheduleKey}`;
export type AdjustDeliveryScheduleValues = {
  [AdjustDeliveryScheduleKey.KIND]: "adjust-delivery-schedule";
  [AdjustDeliveryScheduleKey.FREQUENCY]: string;
};
export type AdjustDeliveryScheduleSubmit =
  FormikSubmitStep<AdjustDeliveryScheduleValues>;

export type OrderSummaryItem =
  SnakeToCamelCaseNested<OrderSummaryItemSnakeCase>;

/**
 * @see https://www.notion.so/literati/Order-Summary-Line-Items-5b0f68db9a47482e9c796afd51fb4444?pvs=4
 * @note required vs. un-required based on common use response payload
 */
export type NormalizedOrderSummary = {
  item: OrderSummaryItem & { affiliateCommissionText?: string };
  subtotal: OrderSummaryItem;
  total: OrderSummaryItem;

  balanceDue?: OrderSummaryItem;
  charge?: OrderSummaryItem;
  coupon?: OrderSummaryItem;
  credit?: OrderSummaryItem;
  outstanding?: OrderSummaryItem;
  shipping?: OrderSummaryItem;
  remainingCredit?: OrderSummaryItem;
  tax?: OrderSummaryItem;
  totalSavings?: OrderSummaryItem;
  voucherApplied?: OrderSummaryItem;
  voucherRemaining?: OrderSummaryItem;
};

export enum PaymentKey {
  KIND = "kind",
  INVOICE = "invoice",
  SUBSCRIPTION = "subscription",
  PHONE_NUMBER = "phoneNumber",
}
export type PaymentKeyEnumValue = `${PaymentKey}`;
export type PaymentValues = {
  [PaymentKey.KIND]: "payment";
  [PaymentKey.INVOICE]: Partial<Invoice>;
  [PaymentKey.SUBSCRIPTION]: Subscription | null;
  [PaymentKey.PHONE_NUMBER]?: string;
};
export type PaymentValuesPartial = Partial<PaymentValues>;
export type PaymentSubmit = FormikSubmitStep<PaymentValues>;

export enum RedeemKey {
  KIND = "kind",
  CODE = "code",
}
export type RedeemKeyEnumValue = `${RedeemKey}`;
export type RedeemValues = {
  [RedeemKey.KIND]: "code";
  [RedeemKey.CODE]: string;
};
export type RedeemSubmit = FormikSubmitStep<RedeemValues>;
